import moment from 'moment'
import { Locale, Translation } from 'locale'
import { CollectionDetail } from 'api/local'
import { AuthorisedUser, Collection, CollectionType } from 'api/generated'
import { compareInsensitive } from 'utils'
import { getUserInitials } from './userUtils'
import { CollectionTypeAbbreviatedHelper } from './DataHelpers'

export const UnassignedCollectionId = 'unassigned'
export const UnassignedCollectionName = Locale.translate(Translation.TC_COLLECTION_UNASSIGNED)

export const newCollectionDetail = (collectionId: string, collectionName: string): CollectionDetail => {
  return {
    collectionId,
    collectionName,
    totalItems: 0,
    subTotals: [0, 0, 0]
  }
}

const compareCollectionDetails = (collection1: CollectionDetail, collection2: CollectionDetail): number => {
  if (collection1.collectionName === UnassignedCollectionName) {
    return -1
  }
  if (collection2.collectionName === UnassignedCollectionName) {
    return 1
  }
  return compareInsensitive(collection1.collectionName, collection2.collectionName)
}

export const toCollectionDetails = (
  collectionRecords: Record<string, CollectionDetail>,
  selectedCollectionId: string | null,
  getCollection: (collectionId: string) => Collection | null
) => {
  // if we only have the unassigned collection, remove it - top level navigation takes care of this case
  const collectionIds = Object.keys(collectionRecords)
  if (collectionIds.length === 1 && collectionIds[0] === UnassignedCollectionId && selectedCollectionId !== UnassignedCollectionId) {
    collectionRecords = {}
  }

  // if the user has a collection selected from which they have removed all the records, show it until it is unselected
  if (selectedCollectionId !== null && !collectionRecords[selectedCollectionId]) {
    const collection = getCollection(selectedCollectionId)
    collectionRecords[selectedCollectionId] = {
      collectionId: selectedCollectionId,
      collectionName: collection ? collection.name : UnassignedCollectionName,
      totalItems: 0,
      subTotals: [0, 0, 0]
    }
  }

  return Object.values(collectionRecords).sort(compareCollectionDetails)
}

export const enabledCollectionTypes = [
  CollectionType.SEND_INDIVIDUALLY,
  CollectionType.ALL_OR_NOTHING,
  CollectionType.ONE_CANCELS_OTHERS,
  CollectionType.ALL_OR_NOTHING_X
]

export const isAonOrOcoCollection = (collectionType: CollectionType | null | undefined) =>
  collectionType && (collectionType === CollectionType.ALL_OR_NOTHING || collectionType === CollectionType.ONE_CANCELS_OTHERS)

export const isAonOrOcoOrSmartGroupCollection = (collectionType: CollectionType | null | undefined) =>
  collectionType &&
  (collectionType === CollectionType.ALL_OR_NOTHING ||
    collectionType === CollectionType.ONE_CANCELS_OTHERS ||
    collectionType === CollectionType.SMART_GROUPING)

export const MAX_COLLECTION_SIZE = 100
export const MAX_STANDARD_COLLECTION_SIZE = 200

export function getMaxCollectionSize(collectionType: CollectionType | null | undefined) {
  if (collectionType === CollectionType.SEND_INDIVIDUALLY) {
    return MAX_STANDARD_COLLECTION_SIZE
  }
  return MAX_COLLECTION_SIZE
}

export function getDefaultCollectionName(
  authorisedUser: AuthorisedUser,
  collectionType: CollectionType = CollectionType.SEND_INDIVIDUALLY
) {
  return `${getUserInitials(authorisedUser)}_${CollectionTypeAbbreviatedHelper.getName(collectionType)}_${moment().format(
    'YYYYMMDD_HHmmss'
  )}`
}
